import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  LocalShipping as ShippingIcon,
  PriorityHigh as CriticalIcon,
  Warning as AlertIcon,
  CheckCircle as NormalIcon,
} from "@mui/icons-material";

import { usePageTitle } from "../context/PageTitleContext";
import { useDistributionPending } from "../hooks/useDistributionPending";
import { useCreatePreview } from "../hooks/useDistributionPreview";
import type { StorePendingSummary } from "../services/distributionService";

type PriorityLevel = StorePendingSummary["priority_level"];

const PRIORITY_META: Record<
  PriorityLevel,
  { color: "error" | "warning" | "success"; icon: React.ReactElement; label: string }
> = {
  critical: {
    color: "error",
    icon: <CriticalIcon fontSize="small" />,
    label: "Crítico",
  },
  alert: {
    color: "warning",
    icon: <AlertIcon fontSize="small" />,
    label: "Alerta",
  },
  normal: {
    color: "success",
    icon: <NormalIcon fontSize="small" />,
    label: "Normal",
  },
};

function generateRequestId(): string {
  const rand = globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : Math.random().toString(36).slice(2, 10);
  return `req-${rand}`;
}

/**
 * B2.23 — DistribucionTiendaPage (con paso intermedio de revisión).
 *
 * Lista de tiendas ordenada por `criticality_score` descendente. El
 * click en una fila NO crea pickings directamente: crea una
 * preview-session (POST /distribution/preview) y navega a
 * /compras/distribucion/preview/:id donde el usuario revisa las
 * líneas y aprueba/excluye antes de impactar Odoo 18 producción.
 *
 * Plan 2026_04_25_plan_distribucion_preview §5.4 — el invariante (C)
 * "UX de un click" del plan original 2026-04-23 fue revertido el
 * 2026-04-25 por riesgo de batches incorrectos en Odoo producción.
 */
export function DistribucionTiendaPage(): React.ReactElement {
  const { setTitle } = usePageTitle();
  const navigate = useNavigate();
  const pending = useDistributionPending();
  const createPreview = useCreatePreview();
  const [error, setError] = useState<string | null>(null);
  const [gateBlocked, setGateBlocked] = useState<{ store?: string } | null>(null);

  useEffect(() => {
    setTitle("Distribución — Generar transferencias");
    return () => setTitle("");
  }, [setTitle]);

  const summaries = pending.data ?? [];
  const stats = useMemo(() => {
    const totalLines = summaries.reduce((acc, s) => acc + s.lines_count, 0);
    const totalUnits = summaries.reduce((acc, s) => acc + s.total_units, 0);
    const criticalCount = summaries.filter(
      (s) => s.priority_level === "critical",
    ).length;
    const withDenials = summaries.reduce(
      (acc, s) => acc + s.active_denials_count,
      0,
    );
    return { totalLines, totalUnits, criticalCount, withDenials };
  }, [summaries]);

  async function runPreview(
    input: { store?: string },
    forceOverride?: boolean,
  ): Promise<void> {
    setError(null);
    setGateBlocked(null);
    try {
      const payload: { store?: string; request_id: string; force_override?: boolean } = {
        request_id: generateRequestId(),
      };
      if (input.store) payload.store = input.store;
      if (forceOverride) payload.force_override = true;
      const res = await createPreview.mutateAsync(payload);
      navigate(`/compras/distribucion/preview/${res.session.id}`);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { code?: string } } };
      if (axiosErr.response?.status === 409 && axiosErr.response.data?.code === "DISTRIBUTION_GATE") {
        setGateBlocked(input);
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    }
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <ShippingIcon color="primary" />
          <Typography variant="h4" component="h1" fontWeight={600}>
            Distribución — Generar transferencias en Odoo
          </Typography>
        </Stack>

        <Alert severity="info" variant="outlined">
          Las tiendas se ordenan por <strong>criticality_score</strong>{" "}
          (déficit, antigüedad de denegaciones, ABC, stockout reciente,
          capacidad). Click en una tienda abre la <strong>revisión previa</strong>{" "}
          con el detalle línea por línea — desde ahí se aprueba o excluye antes
          de crear los pickings en Odoo 18 producción.
        </Alert>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap">
            <Stat label="Tiendas con líneas" value={summaries.length} />
            <Stat label="Críticas" value={stats.criticalCount} />
            <Stat label="Líneas totales" value={stats.totalLines} />
            <Stat label="Unidades totales" value={stats.totalUnits} />
            <Stat label="Denegaciones activas" value={stats.withDenials} />
          </Stack>
        </Paper>

        {gateBlocked && (
          <Alert
            severity="warning"
            onClose={() => setGateBlocked(null)}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void runPreview(gateBlocked, true)}
              >
                Reintentar con override
              </Button>
            }
          >
            Ya se aprobó una distribución en este ciclo de sync. Espera al
            siguiente sync o usa override si la operación fue cancelada en Odoo.
          </Alert>
        )}

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Paper variant="outlined">
          <Box sx={{ p: 2, display: "flex", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Pendientes por tienda
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => pending.refetch()}
            >
              Refrescar
            </Button>
          </Box>

          {pending.isLoading && (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </Box>
          )}

          {pending.isError && (
            <Alert severity="error" sx={{ m: 2 }}>
              No se pudo cargar la lista. Intenta de nuevo.
            </Alert>
          )}

          {!pending.isLoading && summaries.length === 0 && (
            <Alert severity="success" sx={{ m: 2 }}>
              No hay recomendaciones pendientes en ninguna tienda.
            </Alert>
          )}

          {summaries.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Prioridad</TableCell>
                    <TableCell>Tienda</TableCell>
                    <TableCell align="right">Score</TableCell>
                    <TableCell align="right">Líneas</TableCell>
                    <TableCell align="right">Unidades</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell align="right">Denegaciones</TableCell>
                    <TableCell align="right">Antigüedad</TableCell>
                    <TableCell align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaries.map((s) => {
                    const meta = PRIORITY_META[s.priority_level];
                    return (
                      <TableRow key={s.store_mxp_code} hover>
                        <TableCell>
                          <Chip
                            size="small"
                            color={meta.color}
                            icon={meta.icon}
                            label={meta.label}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {s.store_mxp_code}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            {s.display_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {s.criticality_score.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">{s.lines_count}</TableCell>
                        <TableCell align="right">{s.total_units}</TableCell>
                        <TableCell align="right">
                          {s.total_value.toLocaleString("es-MX", {
                            style: "currency",
                            currency: "MXN",
                            maximumFractionDigits: 0,
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {s.active_denials_count}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Antigüedad de la denegación más vieja">
                            <span>{s.oldest_denial_days}d</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={
                              createPreview.isPending || s.lines_count === 0
                            }
                            onClick={() =>
                              void runPreview({ store: s.store_mxp_code })
                            }
                          >
                            Revisar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              borderTop: "1px solid rgba(0,0,0,0.12)",
            }}
          >
            <Button
              variant="contained"
              color="primary"
              disabled={createPreview.isPending || summaries.length === 0}
              onClick={() => void runPreview({})}
              startIcon={
                createPreview.isPending ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <ShippingIcon />
                )
              }
            >
              Revisar todas las tiendas
            </Button>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}

function Stat({
  label,
  value,
}: {
  label: string;
  value: number;
}): React.ReactElement {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={600}>
        {value.toLocaleString("es-MX")}
      </Typography>
    </Box>
  );
}
